import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import Seo, { productJsonLd } from "@/shop/Seo";
import { apiGet, fmt } from "@/shop/api";
import { useCart } from "@/shop/CartContext";

export default function EditionProduct() {
  const { slug } = useParams();
  const { addItem } = useCart();
  const [p, setP] = useState(null);
  const [error, setError] = useState(false);
  const [imgIdx, setImgIdx] = useState(0);
  const [qty, setQty] = useState(1);

  useEffect(() => {
    window.scrollTo(0, 0);
    setP(null);
    setError(false);
    setImgIdx(0);
    setQty(1);
    apiGet(`/api/shop/products/${slug}`)
      .then((prod) => {
        setP(prod);
        window.posthog?.capture("product_view", {
          sku: prod.sku, name: prod.name, type: "edition",
        });
      })
      .catch(() => setError(true));
  }, [slug]);

  if (error)
    return (
      <div className="bg-ink text-bone min-h-screen flex flex-col items-center justify-center gap-4">
        <p className="font-serif italic text-2xl">Questa edizione non è più disponibile.</p>
        <Link to="/shop/editions" className="font-mono text-[11px] uppercase tracking-[0.25em] text-ember hover:underline">
          Torna alle Edizioni
        </Link>
      </div>
    );
  if (!p) return <div className="bg-ink min-h-screen" />;

  const images = p.images?.length ? p.images : [{ url: "/photos/brolly-dettaglio.jpg", alt: p.name }];
  const soldOut = p.sold_out || (p.track_inventory && p.inventory_qty <= 0);

  const add = () => {
    addItem(p, qty);
    toast.success(`${p.name} — aggiunto al carrello`);
  };

  return (
    <div className="bg-ink text-bone min-h-screen" data-testid="edition-product">
      <Seo
        title={p.seo_title || `${p.name} — Stillwater Editions`}
        description={p.seo_description || p.short_description || p.description?.slice(0, 155)}
        canonical={`https://stillwatermag.eu/shop/editions/${p.slug}`}
        jsonLd={productJsonLd(p, `/shop/editions/${p.slug}`)}
        ogType="product"
      />

      <div className="px-5 md:px-10 pt-24 md:pt-28 pb-4">
        <Link
          to="/shop/editions"
          className="font-mono text-[10px] uppercase tracking-[0.3em] text-faint hover:text-ember transition-colors"
        >
          ← Editions
        </Link>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-10 lg:gap-16 px-5 md:px-10 pb-16">
        <div>
          <motion.div
            key={imgIdx}
            initial={{ opacity: 0.4 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="aspect-[4/5] md:aspect-[4/3] overflow-hidden border border-ink-border"
          >
            <img src={images[imgIdx].url} alt={images[imgIdx].alt || `${p.name} — immagine ${imgIdx + 1}`} className="h-full w-full object-cover" />
          </motion.div>
          {images.length > 1 && (
            <div className="mt-4 flex gap-3 overflow-x-auto pb-1">
              {images.map((im, i) => (
                <button
                  key={im.url + i}
                  onClick={() => setImgIdx(i)}
                  className={`shrink-0 w-20 md:w-24 aspect-square overflow-hidden border transition-colors ${i === imgIdx ? "border-ember" : "border-ink-border hover:border-faint"}`}
                >
                  <img src={im.url} alt="" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="lg:pt-4">
          <p className="font-mono text-[10px] md:text-xs uppercase tracking-[0.3em] text-ember">
            Editions{p.release_label ? ` — ${p.release_label}` : ""}
          </p>
          <h1 className="mt-3 font-serif font-light text-3xl md:text-5xl tracking-tight leading-tight">
            {p.name}
          </h1>
          <p className="mt-5 font-mono text-xl md:text-2xl text-bone">
            {fmt(p.price)}
            <span className="ml-2 text-[10px] uppercase tracking-[0.2em] text-faint">IVA incl.</span>
          </p>
          {(p.edition_of || p.edition_size > 0) && (
            <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.25em] text-mist" data-testid="edition-of">
              {p.edition_of || (p.edition_size ? `Edition of ${p.edition_size}` : "")}
            </p>
          )}
          {p.release_date && (
            <p className="mt-1.5 font-mono text-[10px] uppercase tracking-[0.25em] text-faint">
              Uscita: {p.release_date}
            </p>
          )}

          {soldOut ? (
            <div className="mt-8 border border-ember text-ember inline-flex font-mono text-xs tracking-[0.4em] px-6 py-3.5">
              SOLD OUT
            </div>
          ) : (
            <button
              data-testid="add-to-cart"
              onClick={add}
              className="mt-8 inline-flex items-center gap-3 bg-ember text-ink font-mono text-[11px] uppercase tracking-[0.25em] px-8 py-4 rounded-full hover:bg-ember-bright transition-colors duration-300"
            >
              <ShoppingBag size={14} strokeWidth={1.5} />
              {p.preorder ? "Preordina" : "Aggiungi al carrello"}
            </button>
          )}

          {p.why_we_stock_it && (
            <div className="mt-10 border-l-2 border-ember bg-ink-surface p-6">
              <p className="font-serif italic text-xl text-ember">La nota della redazione</p>
              <p className="mt-4 text-sm md:text-base text-bone/90 leading-relaxed">{p.why_we_stock_it}</p>
            </div>
          )}
          {p.description && (
            <p className="mt-10 text-sm text-mist leading-relaxed whitespace-pre-line">{p.description}</p>
          )}
        </div>
      </div>
    </div>
  );
}
